import { useIntl, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import {
    Button,
    Box,
    Heading,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    SimpleGrid,
} from '@chakra-ui/react';
import useCommonToast from '../../../../hooks/useCommonToast';
import useItemCreationWizard from '../../../../hooks/useItemCreationWizard';

import NumberInputFormikField from '../../../forms/NumberInputFormikField';

import { isSubmitDisabled } from '../../../../services/utils';
import { calculateRenewableForecasts } from 'services/renewables';
import ComboboxFormikField from 'components/forms/ComboboxFormikField';
import InputFormikField from 'components/forms/InputFormikField';

const CalculateForecastModal = ({ subRenewableId, nameplateCapacity, definition, ...rest }) => {
    const intl = useIntl();
    const { toast } = useCommonToast();

    const { projectOnOffDates } = useItemCreationWizard();

    const forecastTypes = useSelector((state) => state.forecast.types);
    const startingForecastTypeId = forecastTypes.find((type) => type.description === 'Energy').id;

    const projectDates = projectOnOffDates.find((x) => x.subItemId === subRenewableId);

    const formOnlineDateValue = definition?.onlineDate || projectDates?.enteredOnlineDate || '';
    const formOfflineDateValue = definition?.offlineDate || projectDates?.enteredOfflineDate || '';

    const initialFormValues = {
        readonlyOnlineDate: formOnlineDateValue || '',
        readonlyOfflineDate: formOfflineDateValue || '',
        yearlyDegradation: '',
        forecastType: startingForecastTypeId,
    };

    const formLabels = {
        onlineDate: intl.formatMessage({ id: 'common_online_date' }),
        offlineDate: intl.formatMessage({ id: 'common_offline_date' }),
        yearlyDegradation: intl.formatMessage({ id: 'batteries_battery_wizard_annual_degradation' }),
        forecastType: intl.formatMessage({ id: 'common_grid_forecast_type' }),
    };

    const onFormSubmit = async (values) => {
        const { readonlyOnlineDate, readonlyOfflineDate, yearlyDegradation, forecastType } = values;

        const payload = {
            OnlineDate: readonlyOnlineDate,
            OfflineDate: readonlyOfflineDate,
            YearlyDegradation: yearlyDegradation,
            NameplateCapacity: nameplateCapacity,
            ForecastTypeId: forecastType,
        };

        // Call the API to calculate the renewable forecasts
        await calculateRenewableForecasts(subRenewableId, payload)
            .then(() => {
                const successMessage = intl.formatMessage({ id: 'item_creation_wizard_forecast_calculation_success' });
                toast(successMessage);

                rest.onRefresh();
                rest.onClose();
            })
            .catch((error) => {
                // Handle the error and show a toast message
                const errorMessage = intl.formatMessage({ id: 'common_generic_processing_error' });
                toast({ message: errorMessage, status: 'error' });
            });
    };

    return (
        <>
            <Modal {...rest}>
                <ModalOverlay />

                <ModalContent pt={3}>
                    <ModalHeader pr={12}>
                        <Heading as="h3" variant="h3" textTransform="capitalize">
                            <FormattedMessage id="item_creation_wizard_calculate_forecasts" />
                        </Heading>
                    </ModalHeader>
                    <ModalCloseButton mt={4} mr={3} h={6} w={6} />

                    <Formik initialValues={initialFormValues} onSubmit={onFormSubmit}>
                        {({ values, isSubmitting, errors, touched }) => (
                            <Form>
                                <ModalBody borderBottom="1px" borderColor="gray.200">
                                    <SimpleGrid columns={{ xl: 2 }} spacingX={6} spacingY={4} mb={4}>
                                        <NumberInputFormikField
                                            id="yearlyDegradation"
                                            name="yearlyDegradation"
                                            label={formLabels.yearlyDegradation}
                                            isRequired={true}
                                            clampValueOnBlur={true}
                                            keepWithinRange={true}
                                            min={0}
                                            max={100}
                                        />

                                        <ComboboxFormikField
                                            id="forecastType"
                                            name="forecastType"
                                            options={forecastTypes}
                                            valueKey="id"
                                            labelKey="description"
                                            isRequired={true}
                                            isFastField
                                            label={formLabels.forecastType}
                                        />

                                        <InputFormikField
                                            id="readonlyOnlineDate"
                                            name="readonlyOnlineDate"
                                            label={formLabels.onlineDate}
                                            isDisabled={true}
                                            isRequired
                                            isFastField
                                            info={<FormattedMessage id="item_creation_wizard_renewables_online_date" />}
                                        ></InputFormikField>

                                        <InputFormikField
                                            id="readonlyOfflineDate"
                                            name="readonlyOfflineDate"
                                            label={formLabels.offlineDate}
                                            isDisabled={true}
                                            isRequired
                                            isFastField
                                            info={
                                                <FormattedMessage id="item_creation_wizard_renewables_offline_date" />
                                            }
                                        ></InputFormikField>
                                    </SimpleGrid>
                                </ModalBody>

                                <ModalFooter justifyContent="stretch" flexWrap="wrap">
                                    <Box ml="auto">
                                        <Button variant="secondary" onClick={rest.onClose}>
                                            <Box as="span" textTransform="capitalize">
                                                <FormattedMessage id="common_cancel" />
                                            </Box>
                                        </Button>

                                        <Button
                                            type="submit"
                                            isDisabled={isSubmitDisabled({ errors, touched })}
                                            isLoading={isSubmitting}
                                            ml={3}
                                        >
                                            <FormattedMessage id="common_calculate" />
                                        </Button>
                                    </Box>
                                </ModalFooter>
                            </Form>
                        )}
                    </Formik>
                </ModalContent>
            </Modal>
        </>
    );
};

export default CalculateForecastModal;
